"use client";

import React, { FC, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAppSelector } from "@/app/lib/hooks";
import SearchModal from "../../(client-components)/(Header)/SearchModal";
import SectionNewsletter from "../(home)/desktop/SectionNewsletter";
import { useReviewsAggregate } from "../(home)/desktop/useReviewsAggregate";

interface Continent {
  code: string;
  name: string;
  slug?: string;
  nameTranslated?: string;
  image?: string;
}

interface Country {
  code: string;
  slug: string;
  continentId: string;
  numCities?: number;
  numTours?: number;
  nameTranslated?: string;
  name: string;
}

interface FeaturedCity {
  id: number;
  slug: string;
  name: string;
  nameTranslated?: string;
  countryId: string;
  totalTours?: number;
  entry?: { data?: Array<{ title?: string }> };
}

const CONTINENT_EMOJI: Record<string, string> = {
  EU: "🌐",
  AS: "🌏",
  NA: "🌎",
  SA: "🌎",
  AF: "🌍",
  OC: "🏝️",
  AN: "❄️",
};

const flagSrc = (iso?: string): string | null => {
  if (!iso || iso.length !== 2) return null;
  return `/images/flags/${iso.toLowerCase()}.png`;
};

/**
 * /continents — landing page that lists every activated continent with its
 * country / tour counts and a strip of featured cities. Data is fully
 * dynamic from `/api/continents`, `/api/countries`, `/api/featuredCities`
 * (no editorial badges or "à partir de" prices because we don't have an
 * aggregate price endpoint, so we don't fabricate them).
 */
const ContinentsPage: FC = () => {
  const { t } = useTranslation("guydeez");
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage || "fr_FR"
  );

  const [continents, setContinents] = useState<Continent[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  // ALL activated cities used as a fallback bucket when a continent has no
  // admin-curated featured list (random shuffle keeps every region alive).
  const [allCities, setAllCities] = useState<FeaturedCity[]>([]);
  // Admin-curated overrides per continent code — fetched in parallel and
  // takes precedence over the random shuffle whenever the list is non-empty.
  const [curatedByContinent, setCuratedByContinent] = useState<
    Map<string, FeaturedCity[]>
  >(new Map());
  const [searchOpen, setSearchOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 1024);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const base = process.env.NEXT_PUBLIC_SOCKET || "";
    if (!base) return;
    const run = async () => {
      try {
        const [contRes, countryRes, cityRes] = await Promise.all([
          axios.get(`${base}/api/continents`, {
            params: { activated: true, language: selectedLanguage },
          }),
          axios.get(`${base}/api/countries`, {
            params: { activated: true, language: selectedLanguage },
          }),
          axios.get(`${base}/api/cities`, {
            params: { activated: true, language: selectedLanguage },
          }),
        ]);
        if (cancelled) return;
        const conts: Continent[] = Array.isArray(contRes.data) ? contRes.data : [];
        setContinents(conts);
        setCountries(Array.isArray(countryRes.data) ? countryRes.data : []);
        setAllCities(Array.isArray(cityRes.data) ? cityRes.data : []);

        // Fan out one curated-list request per continent. Each row from the
        // API has `{ city: <fullCity> }`, so we unwrap to a plain FeaturedCity.
        const curatedResults = await Promise.all(
          conts.map((c) =>
            axios
              .get(`${base}/api/featured-continent-cities`, {
                params: {
                  continentCode: c.code,
                  language: selectedLanguage,
                  limit: 8,
                },
              })
              .then((r) => ({
                code: c.code,
                rows: Array.isArray(r?.data?.data) ? r.data.data : [],
              }))
              .catch(() => ({ code: c.code, rows: [] })),
          ),
        );
        if (cancelled) return;
        const map = new Map<string, FeaturedCity[]>();
        for (const { code, rows } of curatedResults) {
          const cities = rows
            .map((row: any) => row.city)
            .filter(Boolean) as FeaturedCity[];
          if (cities.length) map.set(code, cities);
        }
        setCuratedByContinent(map);
      } catch (e) {
        // Silent — empty UI is acceptable, no fake fallback.
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [selectedLanguage]);

  // continentId on country === continent.code (uppercase ISO: AF, EU, AS, ...)
  const countriesByContinent = useMemo(() => {
    const map = new Map<string, Country[]>();
    for (const c of countries) {
      if (!c.continentId) continue;
      const list = map.get(c.continentId) || [];
      list.push(c);
      map.set(c.continentId, list);
    }
    return map;
  }, [countries]);

  const citiesByContinent = useMemo(() => {
    const countryToContinent = new Map<string, string>();
    for (const c of countries) {
      if (c.code && c.continentId) countryToContinent.set(c.code, c.continentId);
    }
    // Bucket every city under its continent…
    const buckets = new Map<string, FeaturedCity[]>();
    for (const city of allCities) {
      const cid = countryToContinent.get(city.countryId);
      if (!cid) continue;
      const list = buckets.get(cid) || [];
      list.push(city);
      buckets.set(cid, list);
    }
    // …then for each continent: prefer the admin-curated list if non-empty,
    // otherwise pick 8 random cities (Fisher-Yates partial shuffle) so even
    // un-curated regions show something instead of an empty strip.
    const out = new Map<string, FeaturedCity[]>();
    const continentCodes = new Set<string>([
      ...buckets.keys(),
      ...curatedByContinent.keys(),
    ]);
    continentCodes.forEach((k) => {
      const curated = curatedByContinent.get(k);
      if (curated && curated.length) {
        out.set(k, curated.slice(0, 8));
        return;
      }
      const arr = [...(buckets.get(k) || [])];
      const take = Math.min(8, arr.length);
      for (let i = 0; i < take; i += 1) {
        const j = i + Math.floor(Math.random() * (arr.length - i));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      out.set(k, arr.slice(0, take));
    });
    return out;
  }, [allCities, countries, curatedByContinent]);

  // Show continents that actually have at least one country, sorted by tour
  // count (descending) so the busiest regions surface first.
  const visibleContinents = useMemo(() => {
    return continents
      .filter((c) => (countriesByContinent.get(c.code) || []).length > 0)
      .map((c) => {
        const list = countriesByContinent.get(c.code) || [];
        const tourCount = list.reduce(
          (acc, country) => acc + (country.numTours || 0),
          0
        );
        return { ...c, _tourCount: tourCount, _countryCount: list.length };
      })
      .sort((a, b) => (b._tourCount || 0) - (a._tourCount || 0));
  }, [continents, countriesByContinent]);

  const totalCountries = countries.length;
  const totalTours = countries.reduce((acc, c) => acc + (c.numTours || 0), 0);
  const totalCities = countries.reduce((acc, c) => acc + (c.numCities || 0), 0);
  const reviewsAggregate = useReviewsAggregate();
  // Use Europe's hero image as the /continents hero — it's the busiest
  // continent and has a curated CMS image. If absent, fall back to the
  // first activated continent that has an image at all.
  const heroImage = useMemo(() => {
    const euro = continents.find((c) => c.code === "EU" && c.image);
    if (euro?.image) return euro.image;
    const first = continents.find((c) => !!c.image);
    return first?.image || null;
  }, [continents]);
  const localeFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(selectedLanguage.replace("_", "-"));
    } catch {
      return new Intl.NumberFormat("fr-FR");
    }
  }, [selectedLanguage]);
  const formatCount = (n: number) => {
    if (n >= 1000) {
      const k = n / 1000;
      return `${k.toFixed(k >= 10 ? 0 : 1).replace(".0", "")}k`;
    }
    return localeFormatter.format(n);
  };

  return (
    <>
    <main
      className="gdx"
      style={{
        background: "var(--g-cream)",
        // Mobile: zero outer padding so the hero can bleed full-width and the
        // breadcrumb hugs the green header (per 22-mobile-home.jsx). Inner
        // padding is applied per-section so non-hero blocks keep their gutter.
        // Desktop: horizontal padding mirrors SectionNewsletter's clamp().
        padding: isMobile
          ? "0 0 32px"
          : "48px clamp(20px, 4vw, 56px) 96px",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            fontSize: 13,
            color: "var(--g-ink-500)",
            padding: isMobile ? "12px 18px" : 0,
            marginBottom: isMobile ? 0 : 32,
          }}
        >
          <Link
            href={`/${lang}/${currency}` as any}
            style={{ color: "var(--g-ink-500)", textDecoration: "none" }}
          >
            {t("breadcrumb_home", { defaultValue: "Accueil" })}
          </Link>
          <span>›</span>
          <span style={{ color: "var(--g-burgundy)", fontWeight: 600 }}>
            {t("continents_breadcrumb", { defaultValue: "Continents" })}
          </span>
        </nav>

        {/* Hero — desktop: cream/no image, serif H1 with italic em, stats in
            prose (per 06-desktop-home.jsx). Mobile: image bg with burgundy
            gradient + glassy stat pills (per 22-mobile-home.jsx). */}
        {isMobile ? (
          <section
            style={{
              position: "relative",
              overflow: "hidden",
              minHeight: 240,
              backgroundImage: heroImage
                ? `url("${encodeURI(heroImage)}")`
                : undefined,
              backgroundColor: "#5e1e1e",
              backgroundSize: "cover",
              backgroundPosition: "center",
              color: "white",
              marginBottom: 4,
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background:
                  "linear-gradient(135deg, rgba(94,30,30,.78), rgba(94,30,30,.45))",
              }}
            />
            <div style={{ position: "relative", padding: "28px 18px 32px" }}>
              <div
                style={{
                  fontSize: 10,
                  color: "var(--g-coral-soft)",
                  fontWeight: 700,
                  letterSpacing: "0.12em",
                  marginBottom: 8,
                }}
              >
                {t("continents_eyebrow_mobile", { defaultValue: "DESTINATIONS" })}
              </div>
              <h1
                style={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: "white",
                  lineHeight: 1.1,
                  marginBottom: 8,
                }}
              >
                {t("continents_title_mobile", {
                  continents: visibleContinents.length,
                  countries: totalCountries,
                  defaultValue: "{{continents}} continents.\n{{countries}} pays.",
                })
                  .split("\n")
                  .map((line, i) => (
                    <React.Fragment key={i}>
                      {i > 0 && <br />}
                      {line}
                    </React.Fragment>
                  ))}
              </h1>
              <p style={{ fontSize: 12, color: "rgba(255,255,255,.9)", margin: 0 }}>
                {t("continents_subtitle_mobile", {
                  defaultValue:
                    "Commencez par le continent. Nous vous présentons ensuite les pays, les villes et les guides francophones qui y vivent.",
                })}
              </p>
              {totalCountries > 0 && (
                <div
                  style={{
                    marginTop: 14,
                    display: "flex",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  {[
                    [formatCount(totalCountries), t("continent_page_stat_countries", { defaultValue: "pays" })],
                    [formatCount(totalCities), t("continent_page_stat_cities", { defaultValue: "villes" })],
                    [formatCount(totalTours), t("continent_page_stat_tours", { defaultValue: "tours" })],
                    ...(reviewsAggregate && reviewsAggregate.totalReviews > 0
                      ? [[
                          `${reviewsAggregate.avgRating.toLocaleString(
                            selectedLanguage.replace("_", "-"),
                            { minimumFractionDigits: 1, maximumFractionDigits: 1 },
                          )}★`,
                          t("continent_page_stat_rating", { defaultValue: "note" }),
                        ]]
                      : []),
                  ].map(([v, l]) => (
                    <div
                      key={l}
                      style={{
                        background: "rgba(255,255,255,.18)",
                        padding: "6px 10px",
                        borderRadius: 999,
                        color: "white",
                        fontSize: 11,
                        fontWeight: 600,
                        backdropFilter: "blur(6px)",
                      }}
                    >
                      <b style={{ fontWeight: 800 }}>{v}</b> {l}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        ) : (
          <section style={{ padding: "26px 0 30px" }}>
            <div
              style={{
                fontSize: 11,
                color: "var(--g-coral)",
                fontWeight: 700,
                letterSpacing: "0.14em",
                marginBottom: 10,
              }}
            >
              {t("continents_eyebrow", { defaultValue: "EXPLORER LE MONDE" })}
            </div>
            <h1
              style={{
                fontFamily: "var(--g-font-display)",
                fontSize: 56,
                fontWeight: 700,
                color: "var(--g-burgundy)",
                lineHeight: 1.05,
                margin: 0,
                marginBottom: 14,
                maxWidth: 900,
                letterSpacing: "-0.02em",
              }}
            >
              {t("continents_title_pre", { defaultValue: "Choisissez un" })}{" "}
              <em style={{ fontStyle: "italic" }}>
                {t("continents_title_em", { defaultValue: "continent" })}
              </em>
              {t("continents_title_post", {
                defaultValue: ", on s'occupe du reste",
              })}
            </h1>
            {totalCountries > 0 && (
              <p
                style={{
                  fontSize: 16,
                  color: "var(--g-ink-500)",
                  maxWidth: 720,
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {t("continents_subtitle", {
                  countries: totalCountries.toLocaleString(
                    selectedLanguage.replace("_", "-"),
                  ),
                  tours: totalTours.toLocaleString(
                    selectedLanguage.replace("_", "-"),
                  ),
                  defaultValue:
                    "{{countries}} pays · {{tours}} tours. Sélectionnez votre continent pour découvrir les destinations disponibles.",
                })}
              </p>
            )}
          </section>
        )}

        {visibleContinents.length === 0 ? (
          <p
            style={{
              textAlign: "center",
              color: "var(--g-ink-500)",
              padding: isMobile ? "14px 18px" : 0,
            }}
          >
            {t("continents_empty", { defaultValue: "Aucun continent à afficher pour le moment." })}
          </p>
        ) : isMobile ? (
          <div style={{ padding: "14px 18px 24px" }}>
            <div
              style={{
                fontSize: 15,
                color: "var(--g-burgundy)",
                fontWeight: 800,
                marginBottom: 12,
              }}
            >
              {t("continents_pick_title", { defaultValue: "Choisissez votre continent" })}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {visibleContinents.map((c) => (
                <MobileContinentCard
                  key={c.code}
                  continent={c}
                  tours={c._tourCount}
                  countriesCount={c._countryCount}
                  topCountries={(countriesByContinent.get(c.code) || []).slice(0, 4)}
                />
              ))}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fill, minmax(min(360px, 100%), 1fr))",
              gap: "clamp(16px, 2vw, 24px)",
            }}
          >
            {visibleContinents.map((c) => (
              <ContinentCard
                key={c.code}
                continent={c}
                tours={c._tourCount}
                countriesCount={c._countryCount}
                topCities={citiesByContinent.get(c.code) || []}
              />
            ))}
          </div>
        )}

        {/* Bottom CTA — desktop: search ("Vous savez déjà où vous allez ?"
            per 06-desktop-home.jsx). Mobile: conseiller ("VOUS HÉSITEZ ?"
            per 20-mobile-shared.jsx MobileServiceCTA). */}
        {isMobile ? (
          <div
            style={{
              padding: "28px 18px",
              background: "var(--g-burgundy)",
              color: "white",
            }}
          >
            <div
              style={{
                fontSize: 10,
                color: "var(--g-coral-soft)",
                fontWeight: 700,
                letterSpacing: "0.14em",
                marginBottom: 8,
              }}
            >
              {t("continents_advice_eyebrow", { defaultValue: "VOUS HÉSITEZ ?" })}
            </div>
            <h3
              style={{
                fontSize: 20,
                fontWeight: 800,
                marginBottom: 8,
                lineHeight: 1.2,
              }}
            >
              {t("continents_advice_title", {
                defaultValue:
                  "Discutez avec un conseiller — réponse en quelques minutes",
              })}
            </h3>
            <p
              style={{
                fontSize: 12.5,
                opacity: 0.85,
                marginBottom: 18,
                lineHeight: 1.55,
              }}
            >
              {t("continents_advice_subtitle", {
                defaultValue:
                  "Notre équipe basée à Paris connaît chaque destination et tous nos guides locaux. On vous aide à choisir le bon tour, sans engagement.",
              })}
            </p>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="gdx-btn"
              style={{
                background: "var(--g-coral)",
                color: "white",
                height: 46,
                padding: "0 18px",
                fontWeight: 700,
                fontSize: 13.5,
                width: "100%",
                border: "none",
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              {t("continents_advice_cta", { defaultValue: "💬 Démarrer la conversation" })}
            </button>
          </div>
        ) : (
          <div
            style={{
              marginTop: 72,
              padding: "48px 32px",
              background: "var(--g-burgundy)",
              color: "white",
              borderRadius: 20,
              textAlign: "center",
            }}
          >
            <h2
              style={{
                fontSize: 28,
                fontWeight: 700,
                fontFamily: "var(--g-font-display)",
                marginBottom: 12,
              }}
            >
              {t("continents_already_know_title", {
                defaultValue: "Vous savez déjà où vous allez ?",
              })}
            </h2>
            <p
              style={{
                fontSize: 14,
                color: "rgba(255,255,255,.75)",
                marginBottom: 24,
                maxWidth: 540,
                marginLeft: "auto",
                marginRight: "auto",
              }}
            >
              {t("continents_already_know_subtitle", {
                defaultValue:
                  "Tapez directement votre destination — on vous propose les meilleurs guides locaux.",
              })}
            </p>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="gdx-btn"
              style={{
                background: "var(--g-coral)",
                color: "white",
                fontWeight: 700,
                border: "none",
                padding: "14px 28px",
                borderRadius: 999,
                cursor: "pointer",
              }}
            >
              {t("continents_search_cta", { defaultValue: "Rechercher une destination" })}
            </button>
          </div>
        )}
      </div>

      <SearchModal
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </main>
    <SectionNewsletter />
    </>
  );
};

const ContinentCard: FC<{
  continent: Continent & { _tourCount?: number; _countryCount?: number };
  tours: number | undefined;
  countriesCount: number | undefined;
  topCities: FeaturedCity[];
}> = ({ continent, tours, countriesCount, topCities }) => {
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const { t } = useTranslation("guydeez");
  const href = `/${lang}/${currency}/continent/${continent.slug || continent.code.toLowerCase()}`;
  const name = capitalize(continent.nameTranslated) || continent.name;

  return (
    <article
      style={{
        background: "white",
        borderRadius: 18,
        overflow: "hidden",
        border: "1px solid rgba(91,31,58,.06)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Link
        href={href as any}
        style={{
          position: "relative",
          height: 220,
          textDecoration: "none",
          backgroundImage: continent.image
            ? `url("${encodeURI(continent.image)}")`
            : undefined,
          backgroundColor: "#4a6f5a",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "block",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(40,15,15,.05) 0%, rgba(40,15,15,.75) 100%)",
          }}
        />
        {typeof countriesCount === "number" && countriesCount > 0 && (
          <span
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              padding: "5px 12px",
              borderRadius: 999,
              background: "rgba(255,255,255,.95)",
              color: "var(--g-burgundy)",
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {t("continents_card_countries", {
              count: countriesCount,
              defaultValue: `${countriesCount} pays`,
            })}
          </span>
        )}
        <div
          style={{
            position: "absolute",
            left: 22,
            bottom: 18,
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: 32,
              fontWeight: 800,
              fontFamily: "var(--g-font-display)",
              letterSpacing: "-.01em",
              textShadow: "0 2px 16px rgba(0,0,0,.45)",
            }}
          >
            {name}
          </div>
          {typeof tours === "number" && tours > 0 && (
            <div
              style={{
                fontSize: 12,
                fontWeight: 500,
                color: "rgba(255,255,255,.85)",
                marginTop: 4,
              }}
            >
              {t("continents_card_tours", {
                count: tours,
                defaultValue: `${tours} tours`,
              })}
            </div>
          )}
        </div>
      </Link>
      <div style={{ padding: 20 }}>
        {topCities.length > 0 && (
          <>
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "var(--g-ink-500)",
                marginBottom: 10,
              }}
            >
              {t("continents_card_featured_cities", { defaultValue: "VILLES PHARES" })}
            </div>
            {/* Cap the chip area to exactly 2 rows so cards stay the same
                height regardless of how many chips actually fit. Chip is
                ~26px tall + 6px row gap → 58px clips any overflowing 3rd row. */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 6,
                marginBottom: 18,
                maxHeight: 58,
                overflow: "hidden",
              }}
            >
              {topCities.map((city) => (
                <Link
                  key={city.slug}
                  href={`/${lang}/${currency}/city/${city.slug}` as any}
                  style={{
                    padding: "5px 11px",
                    borderRadius: 999,
                    background: "var(--g-cream)",
                    border: "1px solid var(--g-cream-deep)",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--g-burgundy)",
                    textDecoration: "none",
                    lineHeight: 1.2,
                  }}
                >
                  {capitalize(cityLocalizedName(city))}
                </Link>
              ))}
            </div>
          </>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingTop: 14,
            borderTop: "1px solid var(--g-cream-deep)",
          }}
        >
          <Link
            href={href as any}
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "var(--g-coral)",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            {t("continents_card_cta", { defaultValue: "Explorer" })} →
          </Link>
        </div>
      </div>
    </article>
  );
};

const MobileContinentCard: FC<{
  continent: Continent & { _tourCount?: number; _countryCount?: number };
  tours: number | undefined;
  countriesCount: number | undefined;
  topCountries: Country[];
}> = ({ continent, tours, countriesCount, topCountries }) => {
  const { lang, currency } = useParams<{ lang: string; currency: string }>();
  const { t } = useTranslation("guydeez");
  const href = `/${lang}/${currency}/continent/${continent.slug || continent.code.toLowerCase()}`;
  const name = capitalize(continent.nameTranslated) || continent.name;
  const emoji = CONTINENT_EMOJI[continent.code.toUpperCase()] || "🌍";
  const remaining = Math.max(0, (countriesCount || 0) - topCountries.length);

  return (
    <article
      className="gdx-card"
      style={{
        background: "white",
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid rgba(91,31,58,.06)",
      }}
    >
      <Link
        href={href as any}
        style={{
          position: "relative",
          display: "block",
          height: 130,
          textDecoration: "none",
          backgroundImage: continent.image
            ? `url("${encodeURI(continent.image)}")`
            : undefined,
          backgroundColor: "#5e1e1e",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, rgba(94,30,30,.55), rgba(94,30,30,.15))",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: 10,
            left: 12,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ fontSize: 24 }}>{emoji}</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "white" }}>
              {name}
            </div>
            <div
              style={{
                fontSize: 10,
                color: "rgba(255,255,255,.9)",
                fontWeight: 600,
              }}
            >
              {typeof countriesCount === "number" && countriesCount > 0
                ? t("continents_card_countries", {
                    count: countriesCount,
                    defaultValue: `${countriesCount} pays`,
                  })
                : null}
              {typeof tours === "number" && tours > 0
                ? ` · ${t("continents_card_tours", {
                    count: tours,
                    defaultValue: `${tours} tours`,
                  })}`
                : null}
            </div>
          </div>
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 10,
            right: 12,
            width: 32,
            height: 32,
            borderRadius: "50%",
            background: "rgba(255,255,255,.95)",
            display: "grid",
            placeItems: "center",
            color: "var(--g-burgundy)",
            fontSize: 14,
            fontWeight: 800,
          }}
        >
          →
        </div>
      </Link>
      <div style={{ padding: "12px 14px" }}>
        {topCountries.length > 0 && (
          <div
            style={{
              display: "flex",
              flexWrap: "nowrap",
              gap: 6,
              overflowX: "auto",
              overflowY: "hidden",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              WebkitOverflowScrolling: "touch",
              alignItems: "center",
            }}
          >
            {topCountries.map((country) => {
              const flag = flagSrc(country.code);
              const label = capitalize(country.nameTranslated) || country.name;
              return (
                <Link
                  key={country.code}
                  href={`/${lang}/${currency}/country/${country.slug}` as any}
                  style={{
                    flex: "0 0 auto",
                    padding: "4px 8px",
                    borderRadius: 999,
                    background: "var(--g-cream)",
                    color: "var(--g-burgundy)",
                    fontSize: 10,
                    fontWeight: 700,
                    textDecoration: "none",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    whiteSpace: "nowrap",
                  }}
                >
                  {flag && (
                    <img
                      src={flag}
                      alt=""
                      width={14}
                      height={10}
                      style={{ display: "block", borderRadius: 1, objectFit: "cover" }}
                    />
                  )}
                  <span>{label}</span>
                </Link>
              );
            })}
            {remaining > 0 && (
              <Link
                href={href as any}
                style={{
                  flex: "0 0 auto",
                  padding: "4px 8px",
                  borderRadius: 999,
                  color: "var(--g-coral)",
                  fontSize: 10,
                  fontWeight: 800,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                +{remaining} →
              </Link>
            )}
          </div>
        )}
      </div>
    </article>
  );
};

// Same fallback chain as the destinations carousel: prefer the CMS-translated
// title, fall back to the raw name when the row is empty or stored as the
// slug placeholder.
const cityLocalizedName = (city: FeaturedCity): string => {
  const title = city.entry?.data?.[0]?.title;
  if (title && title.trim() && title !== city.slug) return title.trim();
  return city.nameTranslated || city.name || "";
};

const capitalize = (s?: string) => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) =>
      w
        .split("-")
        .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
        .join("-")
    )
    .join(" ");
};

export default ContinentsPage;
